/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import appColors from '@sportninja/common/constants/appColors'
import {
  captionXSmall,
  heading3,
  paragraphMedium,
  paragraphSmall,
  paragraphXSmall,
  weightBold,
  weightMedium,
  weightRegular,
  weightSemiBold,
} from '../css'
import { isCanlan } from '@sportninja/common/utils/customer-name'

// Color tokens
export const colors = {
  primary: appColors.PRIMARY,
  background: appColors.SECONDARY_300,
  backgroundLight: appColors.SOFT_STEEL,
  text: appColors.NEUTRAL_0,
  textSecondary: appColors.ATTENDANCE_GRAY,
  border: appColors.SOFT_STEEL,
}

// Container styles
export const containerStyle = css`
  display: flex;
  gap: 24px;
  min-height: 100vh;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`

export const leftPanelStyle = css`
  flex: 2;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex: none;
  }
`

export const rightPanelStyle = css`
  flex: 1;
  min-width: 320px;
  max-width: 411px;

  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    min-width: 0;
    max-width: none;
  }
`

// Video player styles
export const videoPlayerStyle = css`
  width: 100%;
  height: 471px;
  background-color: #000;
  border-radius: 8px;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  @media (max-width: 768px) {
    height: 240px;
  }
`

export const progressPlaceholderStyle = css`
  margin-top: 16px;
  padding: 16px;
  background-color: ${colors.background};
  border-radius: 8px;
  color: ${colors.textSecondary};
  text-align: center;
`

// Game Summary styles
export const gameSummaryContainerStyle = css`
  width: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  display: flex;
  margin-top: 16px;
`

export const gameSummaryTitleStyle = css`
  ${heading3}
  ${weightSemiBold}
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
  align-self: stretch;
  color: ${appColors.NEUTRAL_0};
  word-wrap: break-word;
`

export const gameSummaryContentStyle = css`
  ${paragraphMedium}
  ${weightRegular}
  align-self: stretch;
  color: ${appColors.NEUTRAL_0};
  word-wrap: break-word;

  a {
    color: ${appColors.PRIMARY};
    text-decoration: underline;

    &:hover {
      color: ${appColors.PRIMARY};
      text-decoration: underline;
    }

    &:visited {
      color: ${appColors.PRIMARY};
    }
  }
`

// Card styles
export const cardStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${appColors.SECONDARY_300};
  border-radius: 8px;
  border: 1px solid transparent;
  overflow: hidden;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  margin-bottom: 8px;
  &:hover {
    background-color: ${appColors.SOFT_STEEL};
  }
  &:last-child {
    margin-bottom: 0;
  }
`

export const cardStyleSelected = css`
  ${cardStyle}
  border-color: ${colors.primary};
`

export const cardThumbnailStyle = css`
  width: 120px;
  height: 100%;
  object-fit: cover;
  flex-shrink: 0;
`

export const cardThumbnailEmptyStyle = css`
  width: 120px;
  height: 100%;
  object-fit: contain;
  flex-shrink: 0;
  background-color: ${appColors.NEUTRAL_30};
`

export const cardThumbnailContainerStyle = css`
  position: relative;
  flex-shrink: 0;
  width: 120px;
  align-self: stretch;
`

export const durationBadgeStyle = css`
  position: absolute;
  bottom: 4px;
  right: 4px;
  background-color: rgba(0, 0, 0, 0.6);
  padding-inline: 4px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  color: ${appColors.NEUTRAL_0};
`

export const cardContentStyle = css`
  flex: 1;
  min-width: 0;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const cardPlayerStyle = css`
  ${paragraphSmall}
  ${weightBold}
  color: ${appColors.NEUTRAL_0};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const cardAssistsStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${appColors.NEUTRAL_50};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`

export const cardTimeStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${appColors.NEUTRAL_0};
  flex-shrink: 0;
  line-height: 1;
  text-align: right;
  padding-left: 8px;
`

export const cardTypeStyle = css`
  ${paragraphSmall}
  ${weightBold}
  color: ${appColors.NEUTRAL_0};
  text-align: left;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  margin-bottom: 2px;
`

export const cardTitleStyle = css`
  font-size: 14px;
  ${weightBold}
  color: ${appColors.NEUTRAL_0};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`

export const cardPeriodStyle = css`
  font-size: 12px;
  ${weightRegular}
  color: ${appColors.NEUTRAL_50};
  line-height: 1.2;
`

export const playerChipContainerStyle = css`
  display: flex;
  flex-direction: row;
  gap: 4px;
  flex-wrap: wrap;
`

export const playerChipStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  background-color: ${appColors.SECONDARY_150};
  border-radius: 8px;
  padding: 4px 8px 4px 4px;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

export const playerChipImageStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`

export const playerChipNameStyle = css`
  font-size: 12px;
  ${weightMedium}
  color: ${appColors.NEUTRAL_50};
  white-space: nowrap;
`

export const teamLabelStyle = css`
  ${captionXSmall}
  ${weightBold}
  color: ${appColors.NEUTRAL_0};
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  flex-shrink: 0;
  padding: 8px 4px;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
`

export const homeTeamLabelStyle = css`
  ${teamLabelStyle}
  background-color: ${appColors.SECONDARY_400};
`

export const visitorTeamLabelStyle = css`
  ${teamLabelStyle}
  background-color: ${appColors.SECONDARY_400};
`

export const sideBarStyle = css`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  border-radius: 2px;
`

export const homeSideBarStyle = css`
  ${sideBarStyle}
  background-color: ${colors.primary};
`

export const visitorSideBarStyle = css`
  ${sideBarStyle}
  background-color: #FF6B6B;
`

// Section styles
export const sectionStyle = css`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const sectionTitleStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  justify-content: center;
  display: flex;
  flex-direction: column;
  color: ${appColors.NEUTRAL_50};
  text-transform: uppercase;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  word-wrap: break-word;
  margin-bottom: 12px;
`

// Filter pills styles
export const filterContainerStyle = css`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.backgroundLight};
    border-radius: 2px;
  }
`

export const filterPillStyle = css`
  ${paragraphXSmall}
  ${weightMedium}
  padding: 4px 16px;
  border-radius: 40px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${appColors.SECONDARY_200};
  background-color: ${appColors.SECONDARY_300};
  color: ${appColors.NEUTRAL_50};
  &:hover {
    background-color: ${colors.backgroundLight};
  }
`

export const filterPillActiveStyle = css`
  ${filterPillStyle}
  background-color: ${appColors.PRIMARY};
  color: ${appColors.SECONDARY_300};
  border-color: ${appColors.PRIMARY};
  &:hover {
    background-color: ${appColors.PRIMARY};
  }
`

// Highlights list styles
export const highlightsListStyle = css`
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.backgroundLight};
    border-radius: 2px;
  }
`

// Large card styles for full game and recap
export const largeCardStyle = css`
  display: flex;
  align-items: center;
  background-color: ${appColors.SECONDARY_300};
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  position: relative;

  &:hover {
    background-color: ${appColors.SOFT_STEEL};
  }
`

export const largeCardStyleSelected = css`
  ${largeCardStyle}
  border-color: ${colors.primary};
`

export const largeCardThumbnailStyle = css`
  width: 140px;
  height: 100px;
  border-radius: 4px;
  margin-right: 16px;
  flex-shrink: 0;
  object-fit: contain;
  background-color: ${appColors.NEUTRAL_30};
`

export const largeCardThumbnailWithCoverStyle = css`
  width: 140px;
  height: 100px;
  border-radius: 4px;
  margin-right: 16px;
  flex-shrink: 0;
  object-fit: cover;
  background-color: ${appColors.NEUTRAL_30};
`

export const largeCardContentStyle = css`
  flex: 1;
  min-width: 0;
`

export const largeCardTitleStyle = css`
  ${paragraphSmall}
  ${weightBold}
  color: ${colors.text};
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const largeCardLabelStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${appColors.NEUTRAL_50};
`

export const playingIndicatorStyle = css`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 8px;
  height: 8px;
  background-color: ${colors.primary};
  border-radius: 50%;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(242, 180, 0, 0.7);
    }

    70% {
      transform: scale(1);
      box-shadow: 0 0 0 10px rgba(242, 180, 0, 0);
    }

    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(242, 180, 0, 0);
    }
  }
`
